/**
 * tdesign v0.21.1
 * (c) 2022 tdesign
 * @license MIT
 */

import { _ as _typeof } from '../_chunks/dep-7071345f.mjs';
import { _ as _slicedToArray } from '../_chunks/dep-dcf624da.mjs';
import { toRefs, ref, computed, nextTick, inject, watch, onBeforeMount } from 'vue';
import { getCharacterLength } from '../utils/helper.mjs';
import { FormItemInjectionKey } from '../form/const.mjs';
import useVModel from '../hooks/useVModel.mjs';
import { useFormDisabled } from '../form/hooks.mjs';
import '../_chunks/dep-556a2f2c.mjs';
import '../_chunks/dep-e68fd68c.mjs';
import '../_chunks/dep-0b4c3c44.mjs';
import '../_chunks/dep-8e451077.mjs';
import '../_chunks/dep-a48de419.mjs';
import '../_chunks/dep-a82b01df.mjs';
import '../_chunks/dep-3ca5fb38.mjs';
import '../_chunks/dep-192e10f1.mjs';
import '../_chunks/dep-3d4aad09.mjs';
import '../_chunks/dep-977c5cdd.mjs';
import '../_chunks/dep-c27b1c14.mjs';
import '../_chunks/dep-755716aa.mjs';
import '../_chunks/dep-82798823.mjs';
import '../_chunks/dep-52cbb58a.mjs';
import '../_chunks/dep-db11154e.mjs';
import '../hooks/index.mjs';
import '../hooks/icon.mjs';
import '../hooks/slot.mjs';
import '../hooks/tnode.mjs';
import '../_chunks/dep-40522c7c.mjs';
import '../_chunks/dep-83862ee1.mjs';
import '../_chunks/dep-f6b25712.mjs';
import '../_chunks/dep-59545a4d.mjs';
import '../_chunks/dep-7d96367f.mjs';
import '../_chunks/dep-4889eb51.mjs';
import '../_chunks/dep-0b4185fe.mjs';
import '../_chunks/dep-d2403b2f.mjs';
import '../_chunks/dep-7922050a.mjs';
import '../_chunks/dep-caad2e91.mjs';
import '../_chunks/dep-469bf54b.mjs';
import '../_chunks/dep-baa4f9dc.mjs';
import '../utils/render-tnode.mjs';
import '../_chunks/dep-2684fd15.mjs';
import '../hooks/useConfig.mjs';
import '../config-provider/useConfig.mjs';
import '../_chunks/dep-bfb23a5e.mjs';
import '../_chunks/dep-2a7dc5c7.mjs';
import '../_chunks/dep-9311bf66.mjs';
import '../_chunks/dep-169ea640.mjs';
import '../_chunks/dep-827c4600.mjs';
import '../_chunks/dep-0820e0e2.mjs';
import '../_chunks/dep-8e38f523.mjs';
import '../_chunks/dep-8ef9fb8e.mjs';
import '../_chunks/dep-e769aafd.mjs';
import '../_chunks/dep-400798c5.mjs';
import '../_chunks/dep-361522ee.mjs';
import '../_common/js/global-config/default-config.mjs';
import '../_common/js/global-config/locale/zh_CN.mjs';
import '../config-provider/type.mjs';
import '../hooks/useCommonClassName.mjs';
import '../hooks/useDefaultValue.mjs';
import '../hooks/useDestroyOnClose.mjs';
import '../hooks/useKeepAnimation.mjs';
import '../hooks/useRipple.mjs';
import '../utils/set-style.mjs';
import '../hooks/useVirtualScroll.mjs';

function useInput(props, expose) {
  var _toRefs = toRefs(props),
      value = _toRefs.value,
      modelValue = _toRefs.modelValue;

  var inputValue = ref();
  var clearIconRef = ref(null);
  var innerClickElement = ref();
  var disabled = useFormDisabled();

  var _useVModel = useVModel(value, modelValue, props.defaultValue, props.onChange),
      _useVModel2 = _slicedToArray(_useVModel, 2),
      innerValue = _useVModel2[0],
      setInnerValue = _useVModel2[1];

  var isHover = ref(false);
  var focused = ref(false);
  var renderType = ref(props.type);
  var inputRef = ref(null);
  var inputPreRef = ref(null);
  var limitNumber = ref("");
  var innerStatus = computed(function () {
    if (limitNumber.value) {
      var _limitNumber$value$sp = limitNumber.value.split("/"),
          _limitNumber$value$sp2 = _slicedToArray(_limitNumber$value$sp, 2),
          current = _limitNumber$value$sp2[0],
          total = _limitNumber$value$sp2[1];

      return Number(current) > Number(total) ? "error" : "";
    }

    return "";
  });
  var tStatus = computed(function () {
    if (props.status) return props.status;
    return innerStatus.value;
  });
  var showClear = computed(function () {
    return (innerValue.value && !disabled.value && props.clearable && !props.readonly || props.showClearIconOnEmpty) && isHover.value;
  });

  var focus = function focus() {
    var _inputRef$value;

    return (_inputRef$value = inputRef.value) === null || _inputRef$value === void 0 ? void 0 : _inputRef$value.focus();
  };

  var blur = function blur() {
    var _inputRef$value2;

    return (_inputRef$value2 = inputRef.value) === null || _inputRef$value2 === void 0 ? void 0 : _inputRef$value2.blur();
  };

  var emitFocus = function emitFocus(e) {
    var _props$onFocus;

    inputValue.value = innerValue.value;
    if (props.disabled || props.readonly) return;
    focused.value = true;
    (_props$onFocus = props.onFocus) === null || _props$onFocus === void 0 ? void 0 : _props$onFocus.call(props, innerValue.value, {
      e: e
    });
  };

  var emitClear = function emitClear(_ref) {
    var _props$onClear;

    var e = _ref.e;
    (_props$onClear = props.onClear) === null || _props$onClear === void 0 ? void 0 : _props$onClear.call(props, {
      e: e
    });
    setInnerValue("", {
      e: e
    });
  };

  var onClearIconMousedown = function onClearIconMousedown(e) {
    innerClickElement.value = e.target;
  };

  var emitPassword = function emitPassword() {
    var toggleType = renderType.value === "password" ? "text" : "password";
    renderType.value = toggleType;
  };

  var setInputElValue = function setInputElValue() {
    var v = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "";
    var inputEl = inputRef.value;
    if (!inputEl) return;
    var sV = String(v);

    if (!inputEl.value) {
      return;
    }

    if (inputEl.value !== sV) {
      inputEl.value = sV;
    }
  };

  var inputValueChangeHandle = function inputValueChangeHandle(e) {
    var target = e.target;
    var val = target.value;

    if (props.maxcharacter && props.maxcharacter > 0) {
      var stringInfo = getCharacterLength(val, props.maxcharacter);

      if (_typeof(stringInfo) === "object") {
        if (!props.allowInputOverMax) {
          val = stringInfo.characters;
        }

        limitNumber.value = "".concat(stringInfo.length, "/").concat(props.maxcharacter);
      }
    }

    if (props.maxlength && props.maxlength > 0) {
      if (!props.allowInputOverMax) {
        val = val.substring(0, props.maxlength);
      }

      limitNumber.value = "".concat(val.length, "/").concat(props.maxlength);
    }

    setInnerValue(val, {
      e: e
    });
    nextTick(function () {
      return setInputElValue(innerValue.value);
    });
  };

  var handleInput = function handleInput(e) {
    var checkInputType = e.inputType && e.inputType === "insertCompositionText";
    if (e.isComposing || checkInputType) return;
    inputValueChangeHandle(e);
  };

  var isClearIcon = function isClearIcon() {
    var _clearIconRef$value;

    var tmp = innerClickElement.value;
    if (!tmp || !tmp.tagName || !((_clearIconRef$value = clearIconRef.value) !== null && _clearIconRef$value !== void 0 && _clearIconRef$value.$el) || !["path", "svg"].includes(tmp.tagName)) return false;

    while (tmp) {
      var _clearIconRef$value2;

      if (((_clearIconRef$value2 = clearIconRef.value) === null || _clearIconRef$value2 === void 0 ? void 0 : _clearIconRef$value2.$el) === tmp) {
        return true;
      }

      tmp = tmp.parentNode;
    }

    return false;
  };

  var formItem = inject(FormItemInjectionKey, void 0);

  var formatAndEmitBlur = function formatAndEmitBlur(e) {
    if (props.format) {
      inputValue.value = props.format(innerValue.value);
    }

    focused.value = false;

    if (!isClearIcon() && props.allowTriggerBlur) {
      var _props$onBlur;

      (_props$onBlur = props.onBlur) === null || _props$onBlur === void 0 ? void 0 : _props$onBlur.call(props, innerValue.value, {
        e: e
      });
      formItem === null || formItem === void 0 ? void 0 : formItem.handleBlur();
    }
  };

  var onHandleCompositionend = function onHandleCompositionend(e) {
    var _props$onCompositione;

    inputValueChangeHandle(e);
    (_props$onCompositione = props.onCompositionend) === null || _props$onCompositione === void 0 ? void 0 : _props$onCompositione.call(props, innerValue.value, {
      e: e
    });
  };

  var onHandleCompositionstart = function onHandleCompositionstart(e) {
    var _props$onCompositions;

    (_props$onCompositions = props.onCompositionstart) === null || _props$onCompositions === void 0 ? void 0 : _props$onCompositions.call(props, innerValue.value, {
      e: e
    });
  };

  var onRootClick = function onRootClick(e) {
    var _inputRef$value3;

    (_inputRef$value3 = inputRef.value) === null || _inputRef$value3 === void 0 ? void 0 : _inputRef$value3.focus();
  };

  watch(function () {
    return props.autofocus;
  }, function (value2) {
    if (value2 === true) {
      nextTick(function () {
        var _inputRef$value4;

        (_inputRef$value4 = inputRef.value) === null || _inputRef$value4 === void 0 ? void 0 : _inputRef$value4.focus();
      });
    }
  }, {
    immediate: true
  });
  watch(innerValue, function (v) {
    inputValue.value = v;
  }, {
    immediate: true
  });
  watch(function () {
    return props.type;
  }, function (v) {
    renderType.value = v;
  }, {
    immediate: true
  });
  watch(innerStatus, function () {
    var _props$onValidate;

    (_props$onValidate = props.onValidate) === null || _props$onValidate === void 0 ? void 0 : _props$onValidate.call(props, {
      error: innerStatus.value ? "exceed-maximum" : void 0
    });
  });

  var updateLimitNumber = function updateLimitNumber() {
    if (props.maxcharacter && props.maxcharacter > 0) {
      var len = getCharacterLength(String(innerValue.value));
      limitNumber.value = "".concat(len, "/").concat(props.maxcharacter);
    }

    if (props.maxlength && props.maxlength > 0) {
      limitNumber.value = "".concat(String(innerValue.value).length, "/").concat(props.maxlength);
    }
  };

  onBeforeMount(function () {
    updateLimitNumber();
  });
  expose({
    inputRef: inputRef,
    focus: focus,
    blur: blur
  });
  return {
    isHover: isHover,
    focused: focused,
    renderType: renderType,
    showClear: showClear,
    inputRef: inputRef,
    clearIconRef: clearIconRef,
    inputValue: inputValue,
    limitNumber: limitNumber,
    tStatus: tStatus,
    emitFocus: emitFocus,
    formatAndEmitBlur: formatAndEmitBlur,
    onHandleCompositionend: onHandleCompositionend,
    onHandleCompositionstart: onHandleCompositionstart,
    onRootClick: onRootClick,
    emitPassword: emitPassword,
    handleInput: handleInput,
    emitClear: emitClear,
    onClearIconMousedown: onClearIconMousedown,
    innerValue: innerValue,
    inputPreRef: inputPreRef
  };
}

export { useInput as default };
//# sourceMappingURL=useInput.mjs.map
